/**
 * Polyline utilities: Chaikin smoothing, chaining loose hex-edge segments into polylines, and
 * turning hex-to-hex links (roads, rails) into gently curved paths through the edge midpoints.
 */
import type { HexGrid, HexId } from '@friction/engine';

/** Chaikin corner cutting. `values` (optional, one per point) are subdivided alongside. */
export function chaikin(
  pts: number[],
  iterations: number,
  closed = false,
  values?: number[],
): { pts: number[]; values: number[] | undefined } {
  let p = pts;
  let v = values;
  for (let it = 0; it < iterations; it++) {
    const n = p.length >> 1;
    if (n < 3) break;
    const out: number[] = [];
    const vo: number[] | undefined = v ? [] : undefined;
    if (!closed) {
      out.push(p[0], p[1]);
      vo?.push(v![0]);
    }
    const segs = closed ? n : n - 1;
    for (let i = 0; i < segs; i++) {
      const j = (i + 1) % n;
      const ax = p[i * 2];
      const ay = p[i * 2 + 1];
      const bx = p[j * 2];
      const by = p[j * 2 + 1];
      out.push(ax * 0.75 + bx * 0.25, ay * 0.75 + by * 0.25, ax * 0.25 + bx * 0.75, ay * 0.25 + by * 0.75);
      if (v && vo) vo.push(v[i] * 0.75 + v[j] * 0.25, v[i] * 0.25 + v[j] * 0.75);
    }
    if (!closed) {
      out.push(p[(n - 1) * 2], p[(n - 1) * 2 + 1]);
      vo?.push(v![n - 1]);
    }
    p = out;
    v = vo;
  }
  return { pts: p, values: v };
}

const key = (x: number, y: number): number => Math.round(x * 4) * 131072 + Math.round(y * 4);

export interface Chain {
  pts: number[];
  closed: boolean;
}

/**
 * Join segments [x0, y0, x1, y1, …] that share endpoints into maximal polylines. Chains stop at
 * nodes where the degree is not 2, so T-junctions start new chains.
 */
export function chainSegments(segs: ArrayLike<number>): Chain[] {
  const count = segs.length >> 2;
  const byNode = new Map<number, number[]>();
  const add = (k: number, s: number) => {
    const l = byNode.get(k);
    if (l) l.push(s);
    else byNode.set(k, [s]);
  };
  const ka = new Float64Array(count);
  const kb = new Float64Array(count);
  for (let s = 0; s < count; s++) {
    ka[s] = key(segs[s * 4], segs[s * 4 + 1]);
    kb[s] = key(segs[s * 4 + 2], segs[s * 4 + 3]);
    add(ka[s], s);
    add(kb[s], s);
  }
  const used = new Uint8Array(count);
  const chains: Chain[] = [];
  // Walk from node `k` away through segment `s`; returns the points after the start point.
  const walk = (s: number, fromKey: number, out: number[]): number => {
    let cur = s;
    let from = fromKey;
    for (;;) {
      used[cur] = 1;
      const forward = ka[cur] === from;
      const nx = forward ? segs[cur * 4 + 2] : segs[cur * 4];
      const ny = forward ? segs[cur * 4 + 3] : segs[cur * 4 + 1];
      out.push(nx, ny);
      const nk = forward ? kb[cur] : ka[cur];
      const list = byNode.get(nk)!;
      if (list.length !== 2) return nk;
      const next = list[0] === cur ? list[1] : list[0];
      if (used[next]) return nk;
      cur = next;
      from = nk;
    }
  };
  for (let s = 0; s < count; s++) {
    if (used[s]) continue;
    const fwd: number[] = [];
    const endKey = walk(s, ka[s], fwd);
    const back: number[] = [];
    let startKey = ka[s];
    if (endKey !== ka[s]) {
      const list = byNode.get(ka[s])!;
      if (list.length === 2) {
        const other = list[0] === s ? list[1] : list[0];
        if (!used[other]) startKey = walk(other, ka[s], back);
      }
    }
    const pts: number[] = [];
    for (let i = back.length - 2; i >= 0; i -= 2) pts.push(back[i], back[i + 1]);
    pts.push(segs[s * 4], segs[s * 4 + 1], ...fwd);
    chains.push({ pts, closed: endKey === startKey && pts.length > 4 });
  }
  return chains;
}

/**
 * Chains of hexes linked by a bitmask (roads / rails): maximal runs through degree-2 hexes,
 * breaking at junctions, dead ends and every hex in `stops` (cities).
 */
export function linkChains(grid: HexGrid, mask: Uint8Array, stops: (h: HexId) => boolean): HexId[][] {
  const degree = (h: HexId) => {
    let d = 0;
    for (let k = 0; k < 6; k++) if (mask[h] & (1 << k) && grid.neighbour(h, k) >= 0) d++;
    return d;
  };
  const isNode = (h: HexId) => degree(h) !== 2 || stops(h);
  const done = new Set<number>();
  const edgeKey = (a: HexId, b: HexId) => (a < b ? a * 65536 + b : b * 65536 + a);
  const chains: HexId[][] = [];
  const follow = (start: HexId, first: HexId): HexId[] => {
    const chain = [start, first];
    done.add(edgeKey(start, first));
    let prev = start;
    let cur = first;
    while (!isNode(cur) && cur !== start) {
      let next = -1;
      for (let k = 0; k < 6; k++) {
        const nb = grid.neighbour(cur, k);
        if (nb >= 0 && mask[cur] & (1 << k) && nb !== prev && !done.has(edgeKey(cur, nb))) {
          next = nb;
          break;
        }
      }
      if (next < 0) break;
      done.add(edgeKey(cur, next));
      chain.push(next);
      prev = cur;
      cur = next;
    }
    return chain;
  };
  const visit = (h: HexId) => {
    for (let k = 0; k < 6; k++) {
      const nb = grid.neighbour(h, k);
      if (nb >= 0 && mask[h] & (1 << k) && !done.has(edgeKey(h, nb))) chains.push(follow(h, nb));
    }
  };
  for (let h = 0; h < grid.count; h++) if (mask[h] && isNode(h)) visit(h);
  // pure loops without any junction
  for (let h = 0; h < grid.count; h++) if (mask[h]) visit(h);
  return chains;
}

/**
 * World polyline for a hex chain: straight from the first centre to the first edge midpoint,
 * then quadratic curves (control = hex centre) through successive edge midpoints.
 * `jitter(h)` nudges control points for a hand-drawn feel.
 */
export function curveThroughMidpoints(
  grid: HexGrid,
  chain: HexId[],
  jitter?: (h: HexId) => [number, number],
): number[] {
  const out: number[] = [];
  const cx = (h: HexId) => grid.cx(h);
  const cy = (h: HexId) => grid.cy(h);
  const k = chain.length - 1;
  out.push(cx(chain[0]), cy(chain[0]));
  for (let i = 1; i < k; i++) {
    const a = chain[i - 1];
    const b = chain[i];
    const c = chain[i + 1];
    const [jx, jy] = jitter ? jitter(b) : [0, 0];
    const x0 = (cx(a) + cx(b)) / 2;
    const y0 = (cy(a) + cy(b)) / 2;
    const x1 = cx(b) + jx;
    const y1 = cy(b) + jy;
    const x2 = (cx(b) + cx(c)) / 2;
    const y2 = (cy(b) + cy(c)) / 2;
    const steps = 6;
    for (let s = 0; s <= steps; s++) {
      const t = s / steps;
      const u = 1 - t;
      out.push(u * u * x0 + 2 * u * t * x1 + t * t * x2, u * u * y0 + 2 * u * t * y1 + t * t * y2);
    }
  }
  out.push(cx(chain[k]), cy(chain[k]));
  return out;
}

/** Sample a cubic Bézier into `steps + 1` points (appended to out). */
export function cubic(
  out: number[],
  x0: number,
  y0: number,
  x1: number,
  y1: number,
  x2: number,
  y2: number,
  x3: number,
  y3: number,
  steps: number,
): number[] {
  for (let s = 0; s <= steps; s++) {
    const t = s / steps;
    const u = 1 - t;
    const a = u * u * u;
    const b = 3 * u * u * t;
    const c = 3 * u * t * t;
    const d = t * t * t;
    out.push(a * x0 + b * x1 + c * x2 + d * x3, a * y0 + b * y1 + c * y2 + d * y3);
  }
  return out;
}

/** Total length of a polyline. */
export function polyLength(pts: ArrayLike<number>): number {
  let l = 0;
  for (let i = 2; i < pts.length; i += 2) l += Math.hypot(pts[i] - pts[i - 2], pts[i + 1] - pts[i - 1]);
  return l;
}

/** Point and unit tangent at arc-length `s` along a polyline. */
export function pointAt(pts: ArrayLike<number>, s: number): [x: number, y: number, tx: number, ty: number] {
  let acc = 0;
  for (let i = 2; i < pts.length; i += 2) {
    const dx = pts[i] - pts[i - 2];
    const dy = pts[i + 1] - pts[i - 1];
    const l = Math.hypot(dx, dy);
    if (acc + l >= s || i === pts.length - 2) {
      const t = l > 0 ? Math.min(1, Math.max(0, (s - acc) / l)) : 0;
      return [pts[i - 2] + dx * t, pts[i - 1] + dy * t, l > 0 ? dx / l : 1, l > 0 ? dy / l : 0];
    }
    acc += l;
  }
  return [pts[0] ?? 0, pts[1] ?? 0, 1, 0];
}
